import {AfterViewInit, Component, Input, ChangeDetectionStrategy} from '@angular/core';
import {Heatmap, HeatmapClickPosition} from '../../_types/polls';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {PollsService} from '../../_services/polls.service';
import {take} from 'rxjs';
import {HeatMapData} from '../heatmap/heatmap.component';
import {Community, UserAccessLevel} from '../../_types/communities';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthService} from '../../_services/auth.service';
import {SnackbarService} from "../../_services/snackbar.service";
import {CommunitiesService} from "../../_services/communities.service";

@Component({
    selector: 'app-heatmap-vote',
    templateUrl: './heatmap-vote.component.html',
    styleUrl: './heatmap-vote.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeatmapVoteComponent implements AfterViewInit {
    community: Community | null = null;
    @Input({required: true}) heatmap!: Heatmap;
    @Input() preview: boolean = false;
    @Input() voteButtonText?: string | null;

    overlayOpened: boolean = false;
    coordinates: HeatmapClickPosition | null = null;
    voteForm: FormGroup = this.fb.group({
        option: this.fb.control(null, Validators.required),
        comment: this.fb.control(''),
    })

    loading: boolean = false;
    readonly: boolean = false;
    userClickData: HeatMapData[] = []

    constructor(
        private readonly fb: FormBuilder,
        private readonly polls: PollsService,
        private readonly communitiesService: CommunitiesService,
        private readonly authService: AuthService,
        protected readonly snackbarService: SnackbarService,
    ) {
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => this.community = community);
    }

    get optionFormControl(): FormControl {
        return this.voteForm.get('option') as FormControl;
    }

    get voteButtonLabel(): string {
        const stored = this.heatmap && 'vote_button_text' in this.heatmap
            ? this.heatmap.vote_button_text : this.community?.vote_button_text;
        return this.voteButtonText?.trim() || stored?.trim() || 'Vote';
    }

    get hasTimePassed(): boolean {
        if (!this.heatmap?.end_at) return false
        const now = new Date();
        const targetDate = new Date(this.heatmap?.end_at);
        return targetDate < now;
    }

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    get remainingVotes(): number {
        return this.heatmap.remaining_votes || 0;
    }

    ngAfterViewInit(): void {
        if (this.heatmap) {
            this.heatmap.remaining_votes = this.heatmap.maximum_votes - (this.heatmap.user_vote?.length || 0)
        }
        this.setVotes();
    }

    setVotes(): void {
        this.userClickData = [];
        if (this.heatmap?.user_vote) {
            for (let vote of this.heatmap.user_vote) {
                if (vote?.coordinate_x) {
                    this.userClickData.push({
                        x: Number(vote.coordinate_x),
                        y: Number(vote.coordinate_y),
                        intensity: 1
                    })
                }
            }
            setTimeout((): void => {
                window.dispatchEvent(new Event('resize'));
            }, 200);
        }
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    openVoteOverlay(coords?: HeatmapClickPosition): void {
        if (coords) {
            this.coordinates = coords;
        } else {
            this.readonly = true;
            // this.voteForm.patchValue({
            //     option: this.heatmap.user_vote?.option,
            //     comment: this.heatmap.user_vote?.comment,
            // })
        }
        if (!(this.hasTimePassed || !(this.isSuperuser || this.hasCommunityRole(['member'])) || this.remainingVotes <= 0)) {
            this.overlayOpened = true;
        }
    }

    closeVoteOverlay(): void {
        this.overlayOpened = false;
        this.coordinates = null;
        this.readonly = false;
        this.voteForm.reset();
    }

    setResult(id: number): void {
        if (this.readonly) return;
        this.optionFormControl.setValue(id);
    }

    vote(): void {
        if (this.loading || this.readonly) return;
        this.loading = true;
        const data = {
            ...this.voteForm.value,
            ...this.coordinates,
        }
        this.polls.voteHeatmap(data, this.heatmap.id).pipe(take(1)).subscribe({
            next: (): void => {
                this.loading = false;
                this.closeVoteOverlay();
                if (!this.heatmap.user_vote) {
                    this.heatmap.user_vote = [data]
                } else {
                    this.heatmap.user_vote.push(data);
                }
                this.heatmap.remaining_votes = this.heatmap.maximum_votes - (this.heatmap.user_vote?.length || 0)
                this.setVotes();
            },
            error: (err): void => {
                this.snackbarService.openErrorSnackBar(err || `Sorry an error has occurred please try again later`);
                this.loading = false;
            }
        })
    }
}
